import { useAppDispatch, useAppSelector } from "lib/redux/hooks";
import {
  changeProfileStatus,
  selectResume,
  setResume,
} from "lib/redux/resumeSlice";
import {
  selectScale,
  selectSettings,
  setCurrentSection,
} from "lib/redux/settingsSlice";
import React, { useEffect, useState, useCallback } from "react";
import { useTemplateComponents } from "./Resume/templateUtils";
import {
  useRegisterReactPDFFont,
  useRegisterReactPDFHyphenationCallback,
} from "./fonts/hooks";
import { ResumeIframeCSR } from "./Resume/ResumeIFrame";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import { ChevronLeftIcon, Squares2X2Icon } from "@heroicons/react/24/outline";
import CustomIcon from "./CustomIcon";
import { SafePDFManager } from "./SafePDFManager";
import {
  createResume,
  sendDownloadedResume,
  updateResume,
} from "api/resumeApi";
import ShareIcon from "public/assets/ShareIcon.svg";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { v4 as uuidv4 } from "uuid";
import {
  selectIsPremiumUser,
  setIsAuthenticationOpen,
  setIsExplorePlansOverlayOpen,
  setOpenMobileResumeOverlay,
} from "lib/redux/uiSlice";
import DownloadButton from "./DownloadButton";

interface MobileResumeOverlayProps {
  setOpenShareResumeModal: (visible: boolean) => void;
  setOpenSaveResumePopup: (visible: boolean) => void;
  setOpenTemplatesPreview: (visible: boolean) => void;
  setOpenMobileThemeForm: (visible: boolean) => void;
}

export const MobileResumeOverlay: React.FC<MobileResumeOverlayProps> = ({
  setOpenShareResumeModal,
  setOpenSaveResumePopup,
  setOpenTemplatesPreview,
  setOpenMobileThemeForm,
}) => {
  const dispatch = useAppDispatch();
  const scale = useAppSelector(selectScale);
  const resume = useAppSelector(selectResume);
  const settings = useAppSelector(selectSettings);

  const { TemplateNameToComponent, templateNameToDocument, template } =
    useTemplateComponents(resume, settings);
  const document = templateNameToDocument[template];
  
  // Debug document generation
  useEffect(() => {
    // console.log("Template:", template);
    // console.log("Document:", document);
    // console.log("Resume:", resume);
  }, [template, document, resume]);
  
  // State for PDFManager
  const [pdfInstance, setPdfInstance] = useState<{ url: string | null; loading: boolean; error: string | null } | null>(null);
  const [pdfUpdate, setPdfUpdate] = useState<((newDocument: any) => void) | null>(null);

  // Stable callbacks for PDFManager
  const handlePDFReady = useCallback((instance: { url: string | null; loading: boolean; error: string | null }) => {
    // console.log("PDF Instance received:", instance);
    setPdfInstance(instance);
  }, []);

  const handleUpdateReady = useCallback((update: (newDocument: any) => void) => {
    // console.log("PDF Update function received:", update);
    setPdfUpdate(() => update);
  }, []);

  const fileName = resume.profile.name + " - Resume";
  const accessToken = localStorage.getItem("access-token");

  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [ipAddress, setIpAddress] = useState<string | null>(null);
  const isPremiumUser = useAppSelector(selectIsPremiumUser);

  function onClose() {
    dispatch(setOpenMobileResumeOverlay(false));
  }

  // Get or create deviceId
  useEffect(() => {
    let storedDeviceId = localStorage.getItem("deviceId");
    if (!storedDeviceId) {
      storedDeviceId = uuidv4(); // Generate new UUID for deviceId
      localStorage.setItem("deviceId", storedDeviceId);
    }
    setDeviceId(storedDeviceId);
  }, []);

  // Getting IP Address
  useEffect(() => {
    const getIpAddress = async () => {
      try {
        const res = await fetch("https://api.ipify.org?format=json"); // Free API for IP address
        const data = await res.json();
        setIpAddress(data.ip);
      } catch (error) {
        console.error("Failed to get IP address:", error);
        return "";
      }
    };
    getIpAddress();
  }, []);

  useRegisterReactPDFFont();
  useRegisterReactPDFHyphenationCallback(settings.fontFamily);

  const handleSaveResume = async () => {
    try {
      const access_token = localStorage.getItem("access-token");
      if (!access_token) {
        onClose();
        dispatch(setIsAuthenticationOpen(true));
        return;
      }
      if (resume.id) {
        const updatedResume = await updateResume(resume, resume.id);
        dispatch(setResume(updatedResume));
      } else {
        const createdResume = await createResume(resume);
        dispatch(setResume(createdResume));
      }
      dispatch(changeProfileStatus("IGNORED"));
    } catch (error: any) {
      if (error.code === "TOKEN_EXPIRED") {
        onClose();
        dispatch(setIsAuthenticationOpen(true));
      }
    }
  };

  const handleDownloadResume = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!accessToken) {
      e.preventDefault();
      onClose();
      dispatch(setIsAuthenticationOpen(true));
      return;
    }
  };

  const handleSendDownloadedResume = async () => {
    try {
      const payload = {
        ...resume,
        deviceId: deviceId,
        ipAddress: ipAddress,
        ua: navigator.userAgent,
        screenSize: `${window.screen.width}x${window.screen.height}`,
      };
      await sendDownloadedResume(payload);
    } catch (error) {
      console.debug(error);
    }
  };

  const handleDownloadResumeWithoutAuth = async () => {
    if (!accessToken) {
      // setOpenSaveResumePopup(true);
      dispatch(setIsExplorePlansOverlayOpen(true));
    }
    handleSendDownloadedResume();
    !isPremiumUser && dispatch(setIsExplorePlansOverlayOpen(true));
    // onClose();
    return;
  };

  const handleResumeShare = () => {
    if (!accessToken) {
      onClose();
      dispatch(setIsAuthenticationOpen(true));
      return;
    }
    setOpenShareResumeModal(true);
    onClose();
  };

  const handleSectionChange = (section: string) => {
    dispatch(setCurrentSection(section));
    onClose();
  };

  // Hook to update pdf when document changes
  useEffect(() => {
    if (pdfUpdate) {
      pdfUpdate(document);
    }
  }, [pdfUpdate, document]);

  // Close the modal when clicking outside the popup
  const handleClickOutside = (event: MouseEvent) => {
    const modal = window.document.getElementById("auth-modal");
    const mobileTemplatesForm = window.document.getElementById(
      "mobile-templates-preview"
    );
    const mobileThemeForm = window.document.getElementById("mobile-theme-form");
    if (
      modal &&
      !modal.contains(event.target as Node) &&
      !mobileTemplatesForm?.contains(event.target as Node) &&
      !mobileThemeForm?.contains(event.target as Node)
    ) {
      onClose();
    }
  };

  useEffect(() => {
    window.document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <section className="fixed p-4 inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-70 lg:hidden">
      <div id="auth-modal" className="flex flex-col">
        <div className="mx-auto flex w-[90%] justify-between">
          <button
            className="h-7 w-7 rounded-full bg-white p-[4px] text-xl"
            onClick={onClose}
          >
            <ChevronLeftIcon />
          </button>
          <div className="flex gap-2">
            <div
              className="flex h-7 w-7 items-center justify-center rounded-full bg-white"
              onClick={() => {
                setOpenTemplatesPreview(true);
                setOpenMobileThemeForm(false);
              }}
            >
              <Squares2X2Icon className="h-5 w-5" />
            </div>
            <div
              className="flex h-7 w-7 items-center justify-center rounded-full bg-white"
              onClick={() => {
                setOpenMobileThemeForm(true);
                setOpenTemplatesPreview(false);
              }}
            >
              <CustomIcon iconName="settings" stroke="black" />
            </div>
          </div>
        </div>
        <div className="-mt-2 scale-90">
          <ResumeIframeCSR
            documentSize={settings.documentSize}
            scale={4*scale/5}
            enablePDFViewer={DEBUG_RESUME_PDF_FLAG}
          >
            {resume && TemplateNameToComponent[template]}
          </ResumeIframeCSR>
        </div>
        <div className="mx-auto -mt-2 flex w-[90%] justify-between">
          <button
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white p-2"
            onClick={handleResumeShare}
          >
            <ShareIcon />
          </button>
          <button
            className="flex items-center gap-1 rounded-3xl bg-white px-4 py-2"
            onClick={handleSaveResume}
          >
            <CustomIcon iconName="save" />
            Save
          </button>
          {/* <a
            className="btn-gradient w-fit px-4 py-2 text-sm"
            href={instance.url!}
            // href={accessToken ? instance.url! : "#"}
            download={fileName}
            // download={accessToken ? fileName : "#"}
            // onClick={handleDownloadResume}
            onClick={handleDownloadResumeWithoutAuth}
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            <span className="whitespace-nowrap">Download</span>
          </a> */}
          <DownloadButton
            url={pdfInstance?.url!}
            fileName={fileName}
            onDownload={handleDownloadResumeWithoutAuth}
            onClose={onClose}
          />
          {/* Debug info */}
          {/* {process.env.NODE_ENV === 'development' && (
            <div className="text-xs text-white bg-black p-2 mt-2 rounded">
              <div>PDF URL: {pdfInstance?.url || 'null'}</div>
              <div>Loading: {pdfInstance?.loading?.toString()}</div>
              <div>Error: {pdfInstance?.error || 'none'}</div>
            </div>
          )} */}
        </div>
      </div>
      
      {/* SafePDFManager for handling PDF generation */}
      <SafePDFManager
        document={document}
        onPDFReady={handlePDFReady}
        onUpdateReady={handleUpdateReady}
      />
    </section>
  );
};
